import { safeParseJSON } from "@repo/app-types/helpers/utilities";
import { IconChevronDown, IconChevronUp } from "@tabler/icons-react";
import type { FC } from "react";
import { Button, Disclosure, Heading } from "react-aria-components";
import { twMerge } from "tailwind-merge";

/** One line of a simulated call: a spoken turn, or a tool call with its input and output. */
export interface SimulatedCallTranscriptMessage {
  role: "user" | "assistant" | "tool";
  content: string;
  /** Absent from the retired simulate-voice-agent-conversation's stored transcripts. */
  toolName?: string;
  toolInput: string | null;
}

interface SimulatedCallTranscriptProps {
  messages: readonly SimulatedCallTranscriptMessage[];
}

/** A simulated call's transcript as chat bubbles, tool calls inline. */
export const SimulatedCallTranscript: FC<SimulatedCallTranscriptProps> = (
  props,
) => {
  const { messages } = props;
  if (messages.length === 0) {
    return null;
  }
  return (
    <div className="space-y-1">
      <div className="text-[10px] font-medium text-gray-400">Transcript</div>
      {messages.map((msg, i) => (
        <MessageBubble key={i} msg={msg} />
      ))}
    </div>
  );
};

const MessageBubble: FC<{ msg: SimulatedCallTranscriptMessage }> = ({
  msg,
}) => {
  const renderMessage = () => {
    if (msg.role !== "tool") {
      return <span className="whitespace-pre-wrap">{msg.content}</span>;
    }

    return (
      <div className="space-y-2">
        <div className="font-medium not-italic">Tool: {msg.toolName}</div>
        {msg.toolInput ? (
          <div>
            <div className="mb-1 text-[10px] font-medium text-muted-foreground">
              Input
            </div>
            <ExpandableText text={msg.toolInput} />
          </div>
        ) : null}
        {msg.content ? (
          <div>
            <div className="mb-1 text-[10px] font-medium text-muted-foreground">
              Output
            </div>
            <ExpandableText text={msg.content} />
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div
      className={twMerge(
        "rounded-lg px-2.5 py-1.5 text-xs",
        msg.role === "user" && "bg-indigo-50 text-indigo-900",
        msg.role === "assistant" && "bg-gray-100 text-foreground",
        msg.role === "tool" && "bg-amber-50 text-amber-800",
      )}
    >
      {renderMessage()}
    </div>
  );
};

export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + "...";
}

interface ExpandableTextProps {
  text: string;
  maxPreviewLength?: number;
  className?: string;
}

/**
 * Component that displays text with expand/collapse functionality.
 * Automatically formats JSON strings if detected.
 */
const ExpandableText: FC<ExpandableTextProps> = ({
  text,
  maxPreviewLength = 150,
  className,
}) => {
  // Try to parse as JSON and format it
  const parsedJson = safeParseJSON(text);
  const isJson = parsedJson !== null;
  const formattedText = isJson ? JSON.stringify(parsedJson, null, 2) : text;

  const shouldTruncate = formattedText.length > maxPreviewLength;

  const preClassName = twMerge(
    "font-sans whitespace-pre-wrap",
    isJson && "rounded-sm bg-gray-100 p-2",
    className,
  );

  if (!shouldTruncate) {
    return <pre className={preClassName}>{formattedText}</pre>;
  }

  const truncatedText = truncate(formattedText, maxPreviewLength);

  return (
    <Disclosure>
      {({ isExpanded }) => (
        <div className="space-y-1">
          <pre className={preClassName}>
            {isExpanded ? formattedText : truncatedText}
          </pre>
          <Heading>
            <Button
              slot="trigger"
              className="flex items-center gap-1 text-xs text-gray-600 hover:text-foreground"
            >
              {isExpanded ? (
                <>
                  <IconChevronUp className="h-3 w-3" />
                  Show less
                </>
              ) : (
                <>
                  <IconChevronDown className="h-3 w-3" />
                  Show more
                </>
              )}
            </Button>
          </Heading>
        </div>
      )}
    </Disclosure>
  );
};
